/**
 * Clipboard extraction and import helpers for the panel smart-paste widget.
 */

import { getCsrfToken } from "./image_upload"

const REQUEST_TIMEOUT_MS = 30000

/**
 * Pull both clipboard flavours from a paste event's data transfer.
 * @param {DataTransfer} clipboardData
 * @returns {{html: string, text: string}}
 */
export function extractClipboard(clipboardData) {
  if (!clipboardData) return { html: "", text: "" }
  return {
    html: clipboardData.getData("text/html") || "",
    text: clipboardData.getData("text/plain") || ""
  }
}

/**
 * Count the top-level elements in an HTML string (rough block estimate
 * shown to the user before importing).
 * @param {string} html
 * @returns {number}
 */
export function countTopLevelElements(html) {
  if (!html) return 0
  const template = document.createElement("template")
  template.innerHTML = html
  let count = 0
  template.content.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) count += 1
    else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "") count += 1
  })
  return count
}

/**
 * Estimate how many blocks a plain-text paste would produce (blank-line
 * separated chunks).
 * @param {string} text
 * @returns {number}
 */
export function countTextChunks(text) {
  if (!text) return 0
  return text.split(/\r?\n\s*\r?\n/).filter((chunk) => chunk.trim() !== "").length
}

/**
 * Send captured clipboard content to the import endpoint.
 *
 * Failed requests carry a `retriable` flag: an HTTP error response means the
 * server answered without creating blocks, so the same payload can be safely
 * re-sent; a timeout or network failure means the server may have committed
 * the import anyway, so the caller must not re-send the payload.
 * @param {string} url
 * @param {{html: string, text: string, token: string}} payload
 * @returns {Promise<Object>} Server response body, or { error, retriable } on failure
 */
export async function importPastedContent(url, { html, text, token }) {
  const body = new URLSearchParams()
  if (html) body.append("html", html)
  if (text) body.append("text", text)
  if (token) body.append("import_token", token)

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json"
      },
      body: body.toString(),
      signal: controller.signal
    })

    const responseBody = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { error: responseBody.error || `Import failed (${response.status})`, retriable: true }
    }
    return responseBody
  } catch {
    return { error: "Import failed. Check your connection and try again.", retriable: false }
  } finally {
    clearTimeout(timeout)
  }
}
